import { useTranslation } from 'react-i18next';
import { InteractionReviewStep } from './InteractionReviewStep';
import { useEmployees } from '@/shared/hooks/useEmployees';
import { useValues } from '@/shared/hooks/useValues';
import { useEmployeeContext } from '@/shared/contexts/EmployeeContext';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/shared/components/ui/Button';
import { useGetFeedback } from '@/shared/hooks/useInteractions';

interface FeedbackReviewContentProps {
  feedbackId: string;
  onBack?: () => void;
}

export const FeedbackReviewContent = ({
  feedbackId,
  onBack,
}: FeedbackReviewContentProps) => {
  const { t } = useTranslation('common');
  const currentEmployee = useEmployeeContext();

  const { data: feedback, isLoading: isFeedbackLoading } =
    useGetFeedback(feedbackId);
  const { data: employees, isLoading: isEmployeesLoading } = useEmployees({
    isActive: true,
  });
  const { data: companyValues, isLoading: isValuesLoading } = useValues(
    currentEmployee.companyId
  );

  const isLoading = isFeedbackLoading || isEmployeesLoading || isValuesLoading;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-muted-foreground">{t('loadingFeedback')}</p>
        </div>
      </div>
    );
  }

  if (
    !feedback ||
    !employees ||
    !companyValues ||
    !feedback.formData ||
    Object.keys(feedback.formData).length === 0
  ) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-muted-foreground">{t('notFound')('Feedback')}</p>
          {onBack && (
            <Button variant="outline" onClick={onBack} className="mt-4">
              {t('goBack')}
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8">
      {onBack && (
        <Button variant="ghost" onClick={onBack} className="mb-6 gap-2">
          <ArrowLeft className="h-4 w-4" />
          {t('backToInteractions')}
        </Button>
      )}

      <InteractionReviewStep
        answers={{
          ...feedback.formData,
          'date-selection': new Date(
            feedback.formData?.['date-selection'] || new Date()
          ),
        }}
        employees={employees}
        isSubmitting={false}
        interactionType="feedback"
        companyValues={companyValues}
      />
    </div>
  );
};
